import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Gift, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';

const ReferralsManagement = () => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const el = i18n.language === 'el';
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/admin/referrals`, { headers });
        const rows = Array.isArray(res.data) ? res.data : (res.data?.recent_referrals || []);
        setReferrals(rows);
        setReferralStats(Array.isArray(res.data) ? null : (res.data?.stats || null));
        setError(null);
      } catch (e) {
        console.error(e);
        const message = e.response?.status === 404
          ? (el ? 'Το σύστημα referrals δεν είναι ακόμη ενεργό στο backend.' : 'Referral backend endpoint is not available yet.')
          : (e.response?.data?.detail || (el ? 'Σφάλμα φόρτωσης referrals.' : 'Failed to load referrals.'));
        setError(message);
        toast.error(message);
      }
      finally { setLoading(false); }
    };
    fetchReferrals();
  }, [token]);

  const uniqueReferrers = [...new Set(referrals.map(r => r.referrer_id))];
  const totalReferrals = referralStats?.total ?? referrals.length;
  const activeReferrers = referralStats?.active_referrers ?? uniqueReferrers.length;
  const rewardedReferrals = referralStats?.rewarded ?? referrals.filter(r => r.reward_given).length;

  return (
    <div data-testid="referrals-management">
      <h1 className="font-roboto font-bold text-lg lg:text-2xl text-white mb-6">
        {el ? 'Σύστημα Παραπομπών' : 'Referral System'}
      </h1>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-sm p-4 mb-6 text-sm">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-misel-card border border-white/10 rounded-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <Users size={18} className="text-primary" />
            <span className="text-gray-400 text-sm">{el ? 'Συνολ. Παραπομπές' : 'Total Referrals'}</span>
          </div>
          <p className="text-2xl font-bold text-white" data-testid="total-referrals">{totalReferrals}</p>
        </div>
        <div className="bg-misel-card border border-white/10 rounded-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <Gift size={18} className="text-green-400" />
            <span className="text-gray-400 text-sm">{el ? 'Ενεργοί Referrers' : 'Active Referrers'}</span>
          </div>
          <p className="text-2xl font-bold text-white">{activeReferrers}</p>
        </div>
        <div className="bg-misel-card border border-white/10 rounded-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={18} className="text-yellow-400" />
            <span className="text-gray-400 text-sm">{el ? 'Μετατροπή' : 'Conversion'}</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {rewardedReferrals}/{totalReferrals}
          </p>
        </div>
      </div>

      {/* Referrals List */}
      <div className="bg-misel-card border border-white/10 rounded-sm">
        <div className="p-4 border-b border-white/10">
          <h2 className="text-white font-medium">{el ? 'Πρόσφατες Παραπομπές' : 'Recent Referrals'}</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">{el ? 'Φόρτωση...' : 'Loading...'}</div>
        ) : referrals.length > 0 ? (
          <div className="divide-y divide-white/5">
            {referrals.map(r => (
              <div key={r.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`referral-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-white text-sm break-all">
                    <span className="text-primary">{r.referrer_email}</span>
                    {' → '}
                    <span className="text-green-400">{r.referred_email}</span>
                  </p>
                  <p className="text-xs text-gray-500">{r.created_at?.slice(0, 10)}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${r.reward_given ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {r.reward_given ? (el ? 'Ανταμοιβή' : 'Rewarded') : (el ? 'Εκκρεμεί' : 'Pending')}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            {el ? 'Δεν υπάρχουν παραπομπές ακόμα' : 'No referrals yet'}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralsManagement;
